import { useEffect, useState, useRef } from 'react';
import { Salad, Flame, Clock, RefreshCw, Pill, Utensils, Lock } from 'lucide-react';
import { api, getStoredUser, errMsg, hasEntitlement, getEntitlements } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { LoadingScreen, EmptyState } from '@/components/State';
import ShoppingList from '@/components/ShoppingList';
import LockedFeature from '@/components/LockedFeature';
import { toast } from 'sonner';

const CLIENT_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; }
})();

// Locale-aware date: de-DE -> "16.08.2026", en-US -> "Aug 16, 2026".
// Date-only display (no global timezone logic touched).
const fmtDate = (iso, lang) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const d = new Date(`${s}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return lang === 'en'
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const renderAlt = (a) => {
  if (!a) return null;
  if (Array.isArray(a)) return a.map((x) => x.name || x.items).filter(Boolean).join(' · ');
  return typeof a === 'string' ? a : '';
};

// DE -> German original field, EN -> stored *_en field (falls back to German).
const pick = (obj, key, lang) => (lang === 'en' ? (obj?.[`${key}_en`] || obj?.[key]) : obj?.[key]);

export default function Nutrition() {
  const { t, lang } = useI18n();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);      // full-screen loader: first load only
  const [refreshing, setRefreshing] = useState(false); // in-place refresh (button spinner)
  const [error, setError] = useState(false);          // load failed AND no plan yet -> offer retry
  const [weekIdx, setWeekIdx] = useState(0);
  const [dayIdx, setDayIdx] = useState(0);
  const reqRef = useRef(0);      // sequence counter -> a stale response never overwrites a newer one
  const planRef = useRef(null);  // latest plan, readable inside async callbacks
  const user = getStoredUser();
  const allowed = user?.role === 'admin' || hasEntitlement('nutrition_access');

  // Single source of truth for (re)loading the plan. mode 'refresh' shows a
  // button spinner (never a full-screen loader); a stale/failed response can
  // neither overwrite a newer one (reqRef) nor blank an already-loaded plan.
  const load = async (mode = 'initial') => {
    if (!allowed) { setLoading(false); return; }
    if (mode === 'refresh') {
      if (refreshing) return;            // guard against double refresh
      setRefreshing(true);
    }
    const myReq = ++reqRef.current;
    try {
      const res = await api.get(`/nutrition-plans/${user.id}?tz=${encodeURIComponent(CLIENT_TZ)}&t=${Date.now()}`);
      if (myReq !== reqRef.current) return;             // superseded by a newer request -> drop
      const p = res.data;
      setPlan((prev) => { const next = p || prev; planRef.current = next; return next; });
      setError(false);
      const weeks = p?.weeks || [];
      if (weeks.length) {
        const cur = p?.current_week || 1;
        const idx = weeks.findIndex((w) => (w.number || 0) === cur);
        setWeekIdx((prev) => (idx >= 0 ? idx : (prev < weeks.length ? prev : 0)));
      }
    } catch (e) {
      if (myReq !== reqRef.current) return;             // stale error -> ignore
      // ONCE LOADED -> NEVER BLANK: keep the existing plan; only surface a toast.
      setError(!planRef.current);
      toast.error(errMsg(e));
    } finally {
      if (myReq === reqRef.current) { setLoading(false); setRefreshing(false); }
    }
  };

  useEffect(() => {
    load('initial');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only show the full-screen loader on the very first load. Once a plan has been
  // shown, a later failure must never send the user back to "LÄDT…".
  if (!allowed) return <LockedFeature testid="nutrition-locked" reason={(getEntitlements() || []).length === 0 ? 'package' : 'entitlement'} />;
  if (loading && !plan) return <div className="px-6 pt-10"><LoadingScreen /></div>;

  const weeks = plan?.weeks || [];
  const isMulti = weeks.length > 0;
  const flatMeals = plan?.meals || [];
  if (!plan || (!isMulti && !flatMeals.length)) {
    return (
      <div className="px-6 pt-10">
        <Header t={t} />
        <EmptyState
          icon={Salad}
          title={t('nutrition.title')}
          sub={error ? t('nutrition.loadError') : t('nutrition.empty')}
          action={error ? (
            <button data-testid="nutrition-retry-btn" onClick={() => load('refresh')} disabled={refreshing}
              className="mt-1 inline-flex items-center gap-2 rounded-xl border border-asgym-accent/40 bg-asgym-accent/10 px-4 py-2.5 text-xs font-extrabold tracking-widest text-asgym-accent transition-transform active:scale-95 disabled:opacity-60">
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> {t('nutrition.retry')}
            </button>
          ) : null}
        />
      </div>
    );
  }

  const macros = [
    { label: t('nutrition.kcalDay'), val: plan.daily_calories, unit: '' },
    { label: t('nutrition.protein'), val: plan.daily_protein, unit: 'g' },
    { label: t('nutrition.carbs'), val: plan.daily_carbs, unit: 'g' },
    { label: t('nutrition.fat'), val: plan.daily_fat, unit: 'g' },
  ];
  const supplements = (pick(plan, 'supplements', lang) || '').split('\n').map((s) => s.trim()).filter(Boolean);

  const week = isMulti ? weeks[Math.min(weekIdx, weeks.length - 1)] : null;
  const days = week?.days || [];
  const day = days[Math.min(dayIdx, Math.max(days.length - 1, 0))];
  const dayMeals = isMulti ? (day?.meals || []) : flatMeals;

  return (
    <div className="px-6 pt-10 animate-fade-in" data-testid="client-nutrition">
      <Header t={t} onRefresh={() => load('refresh')} refreshing={refreshing} />

      <div className="grid grid-cols-2 gap-2.5">
        {macros.map((m, i) => (
          <div key={i} className={`rounded-2xl border border-white/[0.06] p-4 ${i === 0 ? 'col-span-2 bg-gradient-to-br from-asgym-accent/15 to-asgym-surface' : 'bg-asgym-surface'}`}>
            <p className="as-label">{m.label}</p>
            <p className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white">{m.val ?? '—'}<span className="ml-0.5 text-base text-asgym-sub">{m.unit}</span></p>
          </div>
        ))}
      </div>

      <p className="as-section mt-8 mb-4">{t('nutrition.meals')}</p>

      {/* Week selector — all weeks visible, locked ones flagged */}
      {isMulti && weeks.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="nutrition-week-tabs">
          {weeks.map((w, i) => {
            const n = w.number || i + 1;
            const isCur = n === (plan.current_week || 1);
            return (
              <button key={i} data-testid={`nutrition-week-tab-${n}`} onClick={() => { setWeekIdx(i); setDayIdx(0); }}
                className={`as-chip whitespace-nowrap ${weekIdx === i ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : w.locked ? 'border-white/[0.06] bg-white/[0.02] text-asgym-muted' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
                {w.locked ? <Lock size={11} /> : (isCur && <span className="h-1.5 w-1.5 rounded-full bg-asgym-accent" />)}
                {t('shop.week')} {n}
              </button>
            );
          })}
        </div>
      )}

      {/* Locked week — visible but not openable */}
      {isMulti && week?.locked ? (
        <div className="as-card flex flex-col items-center gap-2 border-dashed border-asgym-warn/30 bg-asgym-warn/[0.04] p-7 text-center" data-testid="nutrition-week-locked">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-asgym-warn/10 text-asgym-warn"><Lock size={22} /></div>
          <p className="text-[15px] font-extrabold text-white">{t('shop.week')} {week.number}</p>
          <p className="text-[12px] font-bold text-asgym-warn">
            {week.available_from ? `${t('nutrition.availableFrom')} ${fmtDate(week.available_from, lang)}` : t('nutrition.weekLocked')}
          </p>
        </div>
      ) : (
        <>
          {/* Day selector for the (unlocked) week */}
          {isMulti && days.length > 1 && (
            <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="nutrition-day-tabs">
              {days.map((d, i) => (
                <button key={d.id || i} data-testid={`nutrition-day-tab-${i}`} onClick={() => setDayIdx(i)}
                  className={`as-chip whitespace-nowrap ${dayIdx === i ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
                  {(lang === 'en' ? (d.name_en || d.name) : d.name) || `${t('nutrition.day')} ${i + 1}`}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-3">
            {dayMeals.map((meal, i) => <MealCard key={meal.id || i} meal={meal} t={t} lang={lang} />)}
          </div>
        </>
      )}

      {plan.notes && (
        <>
          <p className="as-section mt-8 mb-3">{t('nutrition.notes')}</p>
          <div className="as-card p-4 text-[13px] leading-relaxed text-asgym-sub">{plan.notes}</div>
        </>
      )}

      <ShoppingList clientId={user.id} />

      {supplements.length > 0 && (
        <>
          <p className="as-section mt-8 mb-3 flex items-center gap-2"><Pill size={13} /> {t('nutrition.supplements')}</p>
          <div className="as-card space-y-1 p-4">
            {supplements.map((s, i) => <p key={i} className="text-[13px] text-asgym-sub">• {s}</p>)}
          </div>
        </>
      )}
    </div>
  );
}

function MealCard({ meal, t, lang }) {
  const name = pick(meal, 'name', lang);
  const items = pick(meal, 'items', lang);
  const prep = pick(meal, 'prep', lang);
  const alt = renderAlt(pick(meal, 'alternatives', lang));
  return (
    <div className="as-card p-4" data-testid="meal-card">
      <div className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-asgym-accent/10 text-asgym-accent"><Flame size={16} /></div>
          <div>
            <p className="text-[15px] font-bold text-white">{name}</p>
            {meal.time && <p className="flex items-center gap-1 text-[11px] font-semibold text-asgym-muted"><Clock size={10} /> {meal.time}</p>}
          </div>
        </div>
        <span className="rounded-lg bg-white/[0.04] px-2 py-1 text-[11px] font-bold text-asgym-sub">{meal.calories || 0} kcal</span>
      </div>
      <div className="mb-3 flex gap-3 text-[11px] font-semibold text-asgym-muted">
        <span>P {meal.protein || 0}g</span><span>C {meal.carbs || 0}g</span><span>F {meal.fat || 0}g</span>
      </div>

      {items && (
        <div className="mb-3" data-testid="meal-ingredients">
          <p className="as-label mb-1.5 flex items-center gap-1.5"><Salad size={11} className="text-asgym-accent" /> {t('nutrition.ingredients')}</p>
          <p className="text-[13px] leading-relaxed text-asgym-sub">{items}</p>
        </div>
      )}

      {prep && (
        <div className="mb-3" data-testid="meal-prep">
          <p className="as-label mb-1.5 flex items-center gap-1.5"><Utensils size={11} className="text-asgym-accent" /> {t('nutrition.prep')}</p>
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <p className="whitespace-pre-line text-[12px] leading-relaxed text-asgym-sub">{prep}</p>
          </div>
        </div>
      )}

      {alt && (
        <div data-testid="meal-alternative">
          <p className="as-label mb-1.5 flex items-center gap-1.5"><RefreshCw size={11} className="text-asgym-accent" /> {t('nutrition.alternative')}</p>
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2.5">
            <p className="text-[12px] leading-snug text-asgym-sub">{alt}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const Header = ({ t, onRefresh, refreshing }) => (
  <div className="mb-6 flex items-start justify-between gap-3">
    <div className="min-w-0">
      <p className="as-label">{t('nutrition.label')}</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white">{t('nutrition.title')}</h1>
    </div>
    {onRefresh && (
      <button data-testid="nutrition-refresh-btn" onClick={onRefresh} disabled={refreshing}
        title={t('profile.refresh')} aria-label={t('profile.refresh')}
        className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-asgym-accent/40 bg-asgym-accent/10 text-asgym-accent transition-transform active:scale-95 disabled:opacity-60">
        <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
      </button>
    )}
  </div>
);
